import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api } from "../lib/api";
import {
  resolveGoogleOAuthPopupCompleteUrl,
} from "../lib/config";
import { AppError, logError, toUserMessage } from "../lib/errors";
import {
  emitGoogleConnectMonitorEvent,
  logGoogleConnectAttemptDebug,
} from "../lib/googleConnectMonitor";
import {
  normalizeGoogleAuthorizationUrlForScope,
  validateGoogleConnectResolution,
} from "../lib/googleConnectResolution";
import {
  normalizeGoogleIntegrationStatus,
  type GoogleIntegrationView,
} from "../lib/googleIntegration";
import {
  clearGoogleOAuthPopupSnapshot,
  isGoogleOAuthPopupMessage,
  readGoogleOAuthPopupSnapshot,
  type GoogleOAuthPopupMessage,
} from "../lib/googleOAuthPopup";
import {
  GOOGLE_SCOPE_BUNDLES,
  type GoogleScopeBundle,
  getGoogleScopeBundleLabel,
} from "../lib/googleScopes";

type GoogleConnectUiPhase =
  | "idle"
  | "requesting"
  | "authorizing"
  | "connected"
  | "timeout"
  | "error";

type GoogleLinkCardProps = {
  scopeBundle?: GoogleScopeBundle;
  autoStartToken?: number;
  showServiceSummary?: boolean;
  showConnectButton?: boolean;
  forceConnectedStatus?: boolean;
  forceShowDisconnectButton?: boolean;
  consumePreparedPopup?: () => Window | null;
  onConnected?: (scopeBundle: GoogleScopeBundle, status: GoogleIntegrationView) => void;
  onStatusChange?: (scopeBundle: GoogleScopeBundle, status: GoogleIntegrationView) => void;
  onDisconnected?: (scopeBundle: GoogleScopeBundle) => void;
  onInterrupted?: (scopeBundle: GoogleScopeBundle) => void;
};

const GOOGLE_OAUTH_POPUP_NAME = "wizmate-google-oauth";
const GOOGLE_OAUTH_POPUP_FEATURES = "popup=yes,width=520,height=720";
const GOOGLE_OAUTH_POPUP_FORCE_CLOSE_PATH = "/google-oauth-close.html";
const GOOGLE_CONNECT_POLL_INTERVAL_MS = 1500;
const GOOGLE_CONNECT_POLL_TIMEOUT_MS = 60000;
const GOOGLE_CONNECT_SUCCESS_SETTLE_TIMEOUT_MS = 8000;
const GOOGLE_CONNECT_PREPARE_FAILED_MESSAGE =
  "Google連携の準備に失敗しました。時間をおいて再試行してください。";
const GOOGLE_CONNECT_START_BUTTON_LABEL = "Googleと連携する";
const GOOGLE_DISCONNECT_BUTTON_LABEL = "連携を解除する";


type GoogleLinkDebugEvent = {
  timestamp: string;
  phase: string;
  scopeBundle: GoogleScopeBundle;
  detail?: Record<string, unknown>;
};

function logGoogleLinkDebug(_event: GoogleLinkDebugEvent) {
  // Debug logging disabled.
}

function isJsdomRuntime() {
  return typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent);
}

function createLoadingGoogleStatus(): GoogleIntegrationView {
  return {
    state: "loading",
    scopeBundleGranted: false,
    missingScopes: [],
  };
}

function createErrorGoogleStatus(): GoogleIntegrationView {
  return {
    state: "error",
    scopeBundleGranted: false,
    missingScopes: [],
  };
}

function createConnectedGoogleStatus(): GoogleIntegrationView {
  return {
    state: "connected",
    connectionStatus: "active",
    scopeBundleGranted: true,
    missingScopes: [],
  };
}


function getSelectorScopeActionLabel(scopeBundle: GoogleScopeBundle): string {
  if (scopeBundle === GOOGLE_SCOPE_BUNDLES.bundle) return "まとめて連携";
  return "領収書を連携";
}

function getGuideLines(
  status: GoogleIntegrationView,
  scopeBundle: GoogleScopeBundle,
  showConnectButton: boolean
) {
  const scopeLabel = getGoogleScopeBundleLabel(scopeBundle);
  const selectorScopeActionLabel = getSelectorScopeActionLabel(scopeBundle);
  const isBundleScope = scopeBundle === GOOGLE_SCOPE_BUNDLES.bundle;

  switch (status.state) {
    case "connected":
      return [
        `${scopeLabel} の Google連携は完了しています。`,
        "このまま機能を利用できます。",
      ];
    case "scope_missing":
      return [
        `${scopeLabel} を使うには、追加の同意が必要です。`,
        showConnectButton
          ? `「${GOOGLE_CONNECT_START_BUTTON_LABEL}」を押して、Google画面で許可してください。`
          : `上の「${selectorScopeActionLabel}」を押して、Google画面で許可してください。`,
      ];
    default:
      return [
        isBundleScope
          ? "領収書保存に必要なGoogle権限をまとめて許可します。"
          : (showConnectButton
              ? `「${GOOGLE_CONNECT_START_BUTTON_LABEL}」を押すと、Google連携画面が開きます。`
              : `上の「${selectorScopeActionLabel}」を押すと、Google連携画面が開きます。`),
        isBundleScope
          ? (showConnectButton
              ? `「${GOOGLE_CONNECT_START_BUTTON_LABEL}」を押すと、Google認可画面が開きます。`
              : `上の「${selectorScopeActionLabel}」を押すと、Google認可画面が開きます。`)
          : "連携が完了すると、この画面の状態が自動で更新されます。",
      ];
  }
}

function getGoogleLinkErrorMessage(error: unknown) {
  if (!(error instanceof AppError)) {
    return toUserMessage(error);
  }

  if (error.code === "NOT_FOUND") {
    return "Google連携 API が利用できません。時間を置いて再試行してください。";
  }

  if (error.code === "UNAUTHORIZED") {
    return "認証情報が無効です。再ログイン後にもう一度お試しください。";
  }

  if (error.code === "INVALID_RESPONSE" && error.message === GOOGLE_CONNECT_PREPARE_FAILED_MESSAGE) {
    return GOOGLE_CONNECT_PREPARE_FAILED_MESSAGE;
  }

  return toUserMessage(error);
}


function getGoogleDisconnectErrorMessage(error: unknown) {
  if (!(error instanceof AppError)) {
    return toUserMessage(error);
  }

  if (error.code === "NOT_FOUND") {
    return "Google連携 API が利用できません。時間を置いて再試行してください。";
  }

  if (error.code === "UNAUTHORIZED") {
    return "認証情報が無効です。再ログイン後にもう一度お試しください。";
  }

  if (error.code === "CONFLICT") {
    return "Google連携の状態確認中です。しばらく待ってからもう一度お試しください。";
  }

  return toUserMessage(error);
}

function getGoogleCallbackErrorMessage(errorCode?: string | null) {
  switch (errorCode) {
    case "access_denied":
      return "Google連携がキャンセルされました。もう一度お試しください。";
    case "invalid_state":
      return "Google連携の確認情報が一致しませんでした。もう一度お試しください。";
    case "missing_code":
      return "Googleから認可コードを受け取れませんでした。もう一度お試しください。";
    case "token_exchange_failed":
      return "Google連携の認証に失敗しました。時間を置いて再試行してください。";
    case "missing_refresh_token":
      return "必要な権限の確認ができませんでした。再連携をお試しください。";
    case "missing_required_scope_in_bundle":
      return "領収書保存に必要な権限が不足しています。再連携して、すべての権限を許可してください。";
    default:
      return "Google連携に失敗しました。もう一度お試しください。";
  }
}

function isOAuthCompletionError(payload: GoogleOAuthPopupMessage) {
  return payload.googleConnect === "error" || Boolean(payload.errorCode);
}

function isOAuthCompletionSuccess(payload: GoogleOAuthPopupMessage) {
  return !isOAuthCompletionError(payload);
}

function canDisconnectGoogleIntegration(status: GoogleIntegrationView) {
  return status.state === "connected" || status.state === "scope_missing";
}

function renderPopupLoading(popup: Window) {
  try {
    popup.document.open();
    popup.document.write(`<!doctype html>
<html lang="ja">
  <head>
    <meta charset="utf-8" />
    <title>Google連携を開始しています...</title>
    <style>
      body {
        margin: 0;
        padding: 24px;
        font-family: sans-serif;
        line-height: 1.6;
        color: #4A3E39;
        background: #FFF8F4;
      }
    </style>
  </head>
  <body>
    <p>Google 連携画面を準備しています。しばらくお待ちください。</p>
  </body>
</html>`);
    popup.document.close();
  } catch {
    // noop
  }
}

function tryForceCloseNamedPopup() {
  if (isJsdomRuntime()) {
    return;
  }

  try {
    const closeUrl = new URL(GOOGLE_OAUTH_POPUP_FORCE_CLOSE_PATH, window.location.origin);
    window.open(closeUrl.toString(), GOOGLE_OAUTH_POPUP_NAME, GOOGLE_OAUTH_POPUP_FEATURES);
  } catch {
    // noop
  }
}

export function GoogleLinkCard({
  scopeBundle = GOOGLE_SCOPE_BUNDLES.receipt,
  autoStartToken,
  showServiceSummary = true,
  showConnectButton = true,
  forceConnectedStatus = false,
  forceShowDisconnectButton = false,
  consumePreparedPopup,
  onConnected,
  onStatusChange,
  onDisconnected,
  onInterrupted,
}: GoogleLinkCardProps) {
  const popupRef = useRef<Window | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const popupWatchTimerRef = useRef<number | null>(null);
  const settleTimerRef = useRef<number | null>(null);
  const pollRequestInFlightRef = useRef(false);
  const oauthMessageRef = useRef<GoogleOAuthPopupMessage | null>(null);
  const popupCompletionConsumedRef = useRef(false);
  const lastAutoStartTokenRef = useRef<number | undefined>(undefined);
  const startConnectPollingRef = useRef<() => void>(() => undefined);
  const googleStatusRef = useRef<GoogleIntegrationView>(createLoadingGoogleStatus());

  const [statusRefreshing, setStatusRefreshing] = useState(false);
  const [connectSubmitting, setConnectSubmitting] = useState(false);
  const [disconnectSubmitting, setDisconnectSubmitting] = useState(false);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
  const [connectPhase, setConnectPhase] = useState<GoogleConnectUiPhase>("idle");
  const [statusError, setStatusError] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [disconnectSuccess, setDisconnectSuccess] = useState<string | null>(null);
  const [googleStatus, setGoogleStatus] = useState<GoogleIntegrationView>(createLoadingGoogleStatus());

  const scopeLabel = useMemo(() => getGoogleScopeBundleLabel(scopeBundle), [scopeBundle]);
  const effectiveGoogleStatus =
    forceConnectedStatus && googleStatus.state !== "connected"
      ? createConnectedGoogleStatus()
      : googleStatus;
  const guideLines = useMemo(
    () => getGuideLines(effectiveGoogleStatus, scopeBundle, showConnectButton),
    [effectiveGoogleStatus, scopeBundle, showConnectButton]
  );
  const isBundleScope = scopeBundle === GOOGLE_SCOPE_BUNDLES.bundle;

  const interactionLocked =
    statusRefreshing ||
    googleStatus.state === "loading" ||
    connectPhase === "requesting" ||
    connectPhase === "authorizing";
  const shouldShowConnectButton =
    connectPhase === "requesting" ||
    connectPhase === "authorizing" ||
    effectiveGoogleStatus.state !== "connected";
  const shouldShowDisconnectButton =
    forceShowDisconnectButton || canDisconnectGoogleIntegration(effectiveGoogleStatus);

  useEffect(() => {
    googleStatusRef.current = googleStatus;
  }, [googleStatus]);

  const stopConnectPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollRequestInFlightRef.current = false;
  }, []);

  const stopPopupWatch = useCallback(() => {
    if (popupWatchTimerRef.current !== null) {
      window.clearInterval(popupWatchTimerRef.current);
      popupWatchTimerRef.current = null;
    }
  }, []);

  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const closeConnectPopup = useCallback(() => {
    // popup は完了ページ側で自動クローズするため、親からは参照のみ破棄する。
    popupRef.current = null;
  }, []);

  const startPopupWatch = useCallback(() => {
    stopPopupWatch();
  }, [stopPopupWatch]);

  const resolveBundleConnectedFallbackStatus = useCallback(async (): Promise<GoogleIntegrationView | null> => {
    if (!isBundleScope) {
      return null;
    }

    try {
      const receiptResponse = await api.getGoogleIntegrationStatus(GOOGLE_SCOPE_BUNDLES.receipt);
      const receiptStatus = normalizeGoogleIntegrationStatus(receiptResponse);

      logGoogleLinkDebug({
        timestamp: new Date().toISOString(),
        phase: "bundle_fallback_source_status",
        scopeBundle,
        detail: {
          receiptState: receiptStatus.state,
          receiptConnectionStatus: receiptStatus.connectionStatus ?? null,
        },
      });

      if (receiptStatus.state === "connected") {
        logGoogleLinkDebug({
          timestamp: new Date().toISOString(),
          phase: "bundle_fallback_applied",
          scopeBundle,
          detail: {
            reason: "receipt_connected",
          },
        });
        return createConnectedGoogleStatus();
      }
    } catch (error) {
      logError(error);
      logGoogleLinkDebug({
        timestamp: new Date().toISOString(),
        phase: "bundle_fallback_error",
        scopeBundle,
        detail: {
          message: error instanceof Error ? error.message : String(error),
        },
      });
    }

    return null;
  }, [isBundleScope, scopeBundle]);

  const fetchGoogleStatus = useCallback(
    async (surfaceError: boolean) => {
      try {
        const response = await api.getGoogleIntegrationStatus(scopeBundle);
        const normalizedStatus = normalizeGoogleIntegrationStatus(response);

        const bundleFallbackStatus =
          isBundleScope && normalizedStatus.state !== "connected"
            ? await resolveBundleConnectedFallbackStatus()
            : null;
        const nextStatus = bundleFallbackStatus ?? normalizedStatus;

        logGoogleLinkDebug({
          timestamp: new Date().toISOString(),
          phase: "status_fetched",
          scopeBundle,
          detail: {
            connectionStatus: response.connectionStatus ?? null,
            scopeBundleGranted: response.scopeBundleGranted === true,
            normalizedState: normalizedStatus.state,
            fallbackApplied: Boolean(bundleFallbackStatus),
            finalState: nextStatus.state,
            surfaceError,
          },
        });

        setGoogleStatus(nextStatus);
        onStatusChange?.(scopeBundle, nextStatus);
        if (surfaceError) {
          setStatusError(null);
        }

        return nextStatus;
      } catch (error) {
        logError(error);

        const bundleFallbackStatus = await resolveBundleConnectedFallbackStatus();
        if (bundleFallbackStatus) {
          logGoogleLinkDebug({
            timestamp: new Date().toISOString(),
            phase: "status_fetched_with_fallback_after_error",
            scopeBundle,
            detail: {
              finalState: bundleFallbackStatus.state,
              surfaceError,
              message: error instanceof Error ? error.message : String(error),
            },
          });
          setGoogleStatus(bundleFallbackStatus);
          onStatusChange?.(scopeBundle, bundleFallbackStatus);
          if (surfaceError) {
            setStatusError(null);
          }
          return bundleFallbackStatus;
        }

        if (surfaceError) {
          const nextStatus = createErrorGoogleStatus();
          logGoogleLinkDebug({
            timestamp: new Date().toISOString(),
            phase: "status_fetch_failed",
            scopeBundle,
            detail: {
              finalState: nextStatus.state,
              message: error instanceof Error ? error.message : String(error),
            },
          });
          setGoogleStatus(nextStatus);
          onStatusChange?.(scopeBundle, nextStatus);
          setStatusError("Google連携状態を確認できませんでした。");
        }

        return null;
      }
    },
    [isBundleScope, onStatusChange, resolveBundleConnectedFallbackStatus, scopeBundle]
  );

  const loadGoogleStatus = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) {
        setStatusRefreshing(true);
      }

      const result = await fetchGoogleStatus(true);

      if (showSpinner) {
        setStatusRefreshing(false);
      }

      return result;
    },
    [fetchGoogleStatus]
  );

  const fetchGoogleStatusByScope = useCallback(async (targetScopeBundle: GoogleScopeBundle) => {
    try {
      const response = await api.getGoogleIntegrationStatus(targetScopeBundle);
      return normalizeGoogleIntegrationStatus(response);
    } catch (error) {
      logError(error);
      return null;
    }
  }, []);

  const fetchConnectStatus = useCallback(
    async (targetScopeBundle?: GoogleScopeBundle) => {
      return await fetchGoogleStatusByScope(targetScopeBundle ?? scopeBundle);
    },
    [fetchGoogleStatusByScope, scopeBundle]
  );

  const prepareConnectPopup = useCallback(
    (popup: Window) => {
      popupRef.current = popup;
      oauthMessageRef.current = null;
      clearGoogleOAuthPopupSnapshot();
      stopPopupWatch();
      clearSettleTimer();
      renderPopupLoading(popup);

      setConnectSubmitting(true);
      setConnectPhase("requesting");
      setConnectError(null);
      setStatusError(null);
      setDisconnectError(null);
      setDisconnectSuccess(null);
      setDisconnectConfirmOpen(false);
      popupCompletionConsumedRef.current = false;
    },
    [clearSettleTimer, stopPopupWatch]
  );

  const runGoogleConnectRequest = useCallback(
    async (requestScopeBundle: GoogleScopeBundle, popup: Window) => {
      const response = await api.startGoogleIntegration({
        scopeBundle: requestScopeBundle,
        redirectUrl: resolveGoogleOAuthPopupCompleteUrl(),
      });

      const resolutionValidation = validateGoogleConnectResolution({
        expectedScopeBundle: requestScopeBundle,
        resolvedScopeBundle: response.resolvedScopeBundle,
        resolvedScopes: response.resolvedScopes,
        authorizationUrl: response.authorizationUrl,
      });
      const normalizedAuthorizationUrl = normalizeGoogleAuthorizationUrlForScope(
        response.authorizationUrl,
        requestScopeBundle
      );
      logGoogleConnectAttemptDebug({
        expectedScopeBundle: requestScopeBundle,
        responseScopeBundle: response.scopeBundle,
        resolvedScopeBundle: response.resolvedScopeBundle,
        resolvedScopes: response.resolvedScopes,
        authorizationUrl: normalizedAuthorizationUrl,
        validation: resolutionValidation,
      });

      if (!resolutionValidation.hasResolvedPayload) {
        emitGoogleConnectMonitorEvent({
          type: "oauth_connect_resolution",
          outcome: "warn",
          reason: resolutionValidation.reason,
          expectedScopeBundle: resolutionValidation.expectedScopeBundle,
          resolvedScopeBundle: resolutionValidation.resolvedScopeBundle,
          hasResolvedPayload: resolutionValidation.hasResolvedPayload,
          containsGmailSendScope: resolutionValidation.containsGmailSendScope,
        });
      }

      if (!resolutionValidation.isValid) {
        emitGoogleConnectMonitorEvent({
          type: "oauth_connect_resolution",
          outcome: "error",
          reason: resolutionValidation.reason,
          expectedScopeBundle: resolutionValidation.expectedScopeBundle,
          resolvedScopeBundle: resolutionValidation.resolvedScopeBundle,
          hasResolvedPayload: resolutionValidation.hasResolvedPayload,
          containsGmailSendScope: resolutionValidation.containsGmailSendScope,
        });

        throw new AppError(GOOGLE_CONNECT_PREPARE_FAILED_MESSAGE, "INVALID_RESPONSE");
      }

      try {
        popup.location.replace(normalizedAuthorizationUrl);
        return;
      } catch {
        // Fall through to reopening by name when the retained popup is no longer navigable.
      }

      const opened = window.open(
        normalizedAuthorizationUrl,
        GOOGLE_OAUTH_POPUP_NAME,
        GOOGLE_OAUTH_POPUP_FEATURES
      );
      if (!opened) {
        throw new AppError(
          "Google 連携画面を開けませんでした。ブラウザのポップアップ許可を確認してください。",
          "INVALID_RESPONSE"
        );
      }
    },
    []
  );

  const markConnectSuccess = useCallback(
    (nextStatus?: GoogleIntegrationView | null) => {
      // 成功時は google-link/complete の React UI が window.close() と手動クローズを担う。
      // tryForceCloseNamedPopup() はポップアップを google-oauth-close.html に遷移させるが、
      // COOP 等で window.close() が失敗すると真っ白な画面になるため呼ばない。
      stopConnectPolling();
      stopPopupWatch();
      clearSettleTimer();
      closeConnectPopup();
      clearGoogleOAuthPopupSnapshot();
      oauthMessageRef.current = null;
      popupCompletionConsumedRef.current = true;

      const connectedStatus = nextStatus ?? googleStatusRef.current;
      if (nextStatus) {
        setGoogleStatus(nextStatus);
        onStatusChange?.(scopeBundle, nextStatus);
      }

      setConnectSubmitting(false);
      setConnectPhase("connected");
      setConnectError(null);
      setStatusError(null);
      setDisconnectError(null);
      setDisconnectSuccess(null);

      onConnected?.(scopeBundle, connectedStatus);
    },
    [
      clearSettleTimer,
      closeConnectPopup,
      onConnected,
      onStatusChange,
      scopeBundle,
      stopConnectPolling,
      stopPopupWatch,
    ]
  );

  const markConnectError = useCallback(
    (message: string) => {
      if (popupRef.current && !popupRef.current.closed) {
        tryForceCloseNamedPopup();
      }

      stopConnectPolling();
      stopPopupWatch();
      clearSettleTimer();
      closeConnectPopup();
      clearGoogleOAuthPopupSnapshot();
      popupCompletionConsumedRef.current = true;

      setConnectSubmitting(false);
      setConnectPhase("error");
      setConnectError(message);
      onInterrupted?.(scopeBundle);
    },
    [clearSettleTimer, closeConnectPopup, onInterrupted, scopeBundle, stopConnectPolling, stopPopupWatch]
  );

  const markConnectTimeout = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      tryForceCloseNamedPopup();
    }

    stopConnectPolling();
    stopPopupWatch();
    clearSettleTimer();
    closeConnectPopup();
    clearGoogleOAuthPopupSnapshot();
    popupCompletionConsumedRef.current = true;

    setConnectSubmitting(false);
    setConnectPhase("timeout");
    setConnectError("Google連携の確認に時間がかかっています。もう一度お試しください。");
    onInterrupted?.(scopeBundle);
  }, [clearSettleTimer, closeConnectPopup, onInterrupted, scopeBundle, stopConnectPolling, stopPopupWatch]);

  const completeConnectWithoutSuccess = useCallback(
    (nextStatus?: GoogleIntegrationView | null, errorMessage?: string | null) => {
      if (popupRef.current && !popupRef.current.closed) {
        tryForceCloseNamedPopup();
      }

      stopConnectPolling();
      stopPopupWatch();
      clearSettleTimer();
      closeConnectPopup();
      clearGoogleOAuthPopupSnapshot();
      popupCompletionConsumedRef.current = true;

      if (nextStatus) {
        setGoogleStatus(nextStatus);
        onStatusChange?.(scopeBundle, nextStatus);
      }

      setConnectSubmitting(false);
      setConnectPhase(errorMessage ? "error" : "idle");
      setConnectError(errorMessage ?? null);

      onInterrupted?.(scopeBundle);
    },
    [
      clearSettleTimer,
      closeConnectPopup,
      onInterrupted,
      onStatusChange,
      scopeBundle,
      stopConnectPolling,
      stopPopupWatch,
    ]
  );

  const handleConnectedStatus = useCallback(
    (status: GoogleIntegrationView) => {
      markConnectSuccess(status);
    },
    [markConnectSuccess]
  );

  const scheduleSuccessSettlement = useCallback(() => {
    clearSettleTimer();

    settleTimerRef.current = window.setTimeout(async () => {
      const status = await fetchConnectStatus();

      if (status?.state === "connected") {
        await handleConnectedStatus(status);
        return;
      }

      if (status?.state === "scope_missing") {
        completeConnectWithoutSuccess(status);
        return;
      }

      completeConnectWithoutSuccess(
        status,
        "Google連携の反映を確認できませんでした。時間をおいて再試行してください。"
      );
    }, GOOGLE_CONNECT_SUCCESS_SETTLE_TIMEOUT_MS);
  }, [clearSettleTimer, completeConnectWithoutSuccess, fetchConnectStatus, handleConnectedStatus]);

  const handleOAuthCompletion = useCallback(
    async (payload: GoogleOAuthPopupMessage) => {
      // 同一ステップの完了通知（postMessage と storage イベント）が二重に
      // 届いた場合の多重処理を防ぐ。
      if (popupCompletionConsumedRef.current) {
        return;
      }

      popupCompletionConsumedRef.current = true;
      oauthMessageRef.current = payload;
      // await に入る前にポーリングを止め、API ステータス確認との競合を防ぐ。
      stopConnectPolling();

      if (isOAuthCompletionError(payload)) {
        markConnectError(getGoogleCallbackErrorMessage(payload.errorCode));
        return;
      }

      const status = await fetchConnectStatus();

      if (status?.state === "connected") {
        await handleConnectedStatus(status);
        return;
      }

      if (status?.state === "scope_missing") {
        completeConnectWithoutSuccess(status);
        return;
      }

      setConnectError(null);
      setConnectSubmitting(false);
      setConnectPhase("authorizing");
      scheduleSuccessSettlement();
    },
    [
      completeConnectWithoutSuccess,
      fetchConnectStatus,
      handleConnectedStatus,
      markConnectError,
      scheduleSuccessSettlement,
      stopConnectPolling,
    ]
  );

  const consumeStoredOAuthCompletion = useCallback(async () => {
    const snapshot = readGoogleOAuthPopupSnapshot();
    if (!snapshot) return false;

    clearGoogleOAuthPopupSnapshot();
    await handleOAuthCompletion(snapshot);
    return true;
  }, [handleOAuthCompletion]);

  const startConnectPolling = useCallback(() => {
    const startedAt = Date.now();
    stopConnectPolling();

    pollTimerRef.current = window.setInterval(async () => {
      if (pollRequestInFlightRef.current) {
        return;
      }
      pollRequestInFlightRef.current = true;

      const status = await fetchConnectStatus();

      try {
        if (status?.state === "connected") {
          await handleConnectedStatus(status);
          return;
        }

        const oauthMessage = oauthMessageRef.current;

        if (oauthMessage && isOAuthCompletionSuccess(oauthMessage) && status?.state === "scope_missing") {
          completeConnectWithoutSuccess(status);
          return;
        }

        if (oauthMessage && isOAuthCompletionError(oauthMessage)) {
          markConnectError(getGoogleCallbackErrorMessage(oauthMessage.errorCode));
          return;
        }

        if (Date.now() - startedAt >= GOOGLE_CONNECT_POLL_TIMEOUT_MS) {
          markConnectTimeout();
        }
      } finally {
        pollRequestInFlightRef.current = false;
      }
    }, GOOGLE_CONNECT_POLL_INTERVAL_MS);
  }, [
    completeConnectWithoutSuccess,
    fetchConnectStatus,
    handleConnectedStatus,
    markConnectError,
    markConnectTimeout,
    stopConnectPolling,
  ]);

  useEffect(() => {
    startConnectPollingRef.current = startConnectPolling;
  }, [startConnectPolling]);

  useEffect(() => {
    setConnectPhase("idle");
    setConnectError(null);
    setStatusError(null);
    setDisconnectError(null);
    setDisconnectSuccess(null);
    setDisconnectConfirmOpen(false);
    setConnectSubmitting(false);
    setDisconnectSubmitting(false);
    // Scope switch must not inherit previous scope's connected state.
    setGoogleStatus(createLoadingGoogleStatus());
    oauthMessageRef.current = null;
    popupCompletionConsumedRef.current = false;
    clearGoogleOAuthPopupSnapshot();
    stopConnectPolling();
    stopPopupWatch();
    clearSettleTimer();
    closeConnectPopup();

    void (async () => {
      const consumed = await consumeStoredOAuthCompletion();
      if (!consumed) {
        await loadGoogleStatus();
      }
    })();

    return () => {
      stopConnectPolling();
      stopPopupWatch();
      clearSettleTimer();
      closeConnectPopup();
    };
  }, [
    clearSettleTimer,
    closeConnectPopup,
    consumeStoredOAuthCompletion,
    loadGoogleStatus,
    scopeBundle,
    stopConnectPolling,
    stopPopupWatch,
  ]);

  useEffect(() => {
    const handleFocus = () => {
      void (async () => {
        const consumed = await consumeStoredOAuthCompletion();
        if (!consumed) {
          await loadGoogleStatus(false);
        }
      })();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleFocus();
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== "wizmate.google-oauth-complete" || !event.newValue) return;
      void consumeStoredOAuthCompletion();
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, [consumeStoredOAuthCompletion, loadGoogleStatus]);

  useEffect(() => {
    if (googleStatus.state !== "connected") return;
    if (connectPhase === "idle" || connectPhase === "connected") return;

    markConnectSuccess(googleStatus);
  }, [connectPhase, googleStatus, markConnectSuccess]);

  useEffect(() => {
    logGoogleLinkDebug({
      timestamp: new Date().toISOString(),
      phase: "ui_state_snapshot",
      scopeBundle,
      detail: {
        statusState: googleStatus.state,
        effectiveStatusState: effectiveGoogleStatus.state,
        forceConnectedStatus,
        connectPhase,
        canDisconnect: canDisconnectGoogleIntegration(effectiveGoogleStatus),
        shouldShowDisconnectButton,
        shouldShowConnectButton,
        interactionLocked,
      },
    });
  }, [
    connectPhase,
    effectiveGoogleStatus,
    forceConnectedStatus,
    googleStatus,
    interactionLocked,
    scopeBundle,
    shouldShowDisconnectButton,
    shouldShowConnectButton,
  ]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin) return;
      if (!isGoogleOAuthPopupMessage(event.data)) return;

      const payload = event.data;
      if (
        payload.scopeBundle &&
        payload.scopeBundle !== scopeBundle
      ) {
        return;
      }

      void handleOAuthCompletion(payload);
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleOAuthCompletion, scopeBundle]);

  const handleStartGoogleConnect = useCallback(
    async (autoTriggered = false) => {
      const transitionLocked = connectPhase === "requesting" || connectPhase === "authorizing";
      const statusLocked = statusRefreshing || googleStatus.state === "loading";
      const startBlocked = disconnectSubmitting || transitionLocked || (!autoTriggered && statusLocked);
      if (startBlocked) return;

      const popup =
        consumePreparedPopup?.() ??
        window.open("", GOOGLE_OAUTH_POPUP_NAME, GOOGLE_OAUTH_POPUP_FEATURES);

      if (!popup) {
        setConnectPhase(autoTriggered ? "idle" : "error");
        setConnectError(
          autoTriggered
            ? showConnectButton
              ? "次のGoogle連携を自動で開始できませんでした。下の「Googleと連携する」を押してください。"
              : "次のGoogle連携を自動で開始できませんでした。サービスの連携ボタンをもう一度押してください。"
            : "Google 連携画面を開けませんでした。ブラウザのポップアップ許可を確認してください。"
        );
        if (autoTriggered) {
          onInterrupted?.(scopeBundle);
        }
        return;
      }

      prepareConnectPopup(popup);

      try {
        await runGoogleConnectRequest(scopeBundle, popup);
        setConnectSubmitting(false);
        setConnectPhase("authorizing");
        startPopupWatch();
        startConnectPolling();
      } catch (error) {
        logError(error);
        markConnectError(getGoogleLinkErrorMessage(error));
      }
    },
    [
      disconnectSubmitting,
      connectPhase,
      statusRefreshing,
      googleStatus.state,
      markConnectError,
      prepareConnectPopup,
      runGoogleConnectRequest,
      scopeBundle,
      showConnectButton,
      startPopupWatch,
      startConnectPolling,
      consumePreparedPopup,
      onInterrupted,
    ]
  );

  useEffect(() => {
    if (autoStartToken === undefined) return;
    if (lastAutoStartTokenRef.current === autoStartToken) return;

    lastAutoStartTokenRef.current = autoStartToken;
    void handleStartGoogleConnect(true);
  }, [autoStartToken, handleStartGoogleConnect]);

  const handleOpenDisconnectConfirm = () => {
    if (interactionLocked || connectSubmitting || disconnectSubmitting) return;

    setConnectError(null);
    setDisconnectError(null);
    setDisconnectSuccess(null);
    setDisconnectConfirmOpen(true);
  };

  const handleCloseDisconnectConfirm = () => {
    if (disconnectSubmitting) return;
    setDisconnectConfirmOpen(false);
  };

  const handleDisconnectGoogleIntegration = async () => {
    setDisconnectSubmitting(true);
    setDisconnectError(null);
    setConnectError(null);

    try {
      const response = await api.disconnectGoogleIntegration();

      if (!response.disconnected) {
        throw new AppError("Google integration disconnect failed.", "INVALID_RESPONSE");
      }

      setDisconnectSuccess("Google連携を解除しました。必要な場合は再度連携してください。");
      setDisconnectConfirmOpen(false);
      await loadGoogleStatus(false);
      onDisconnected?.(scopeBundle);
    } catch (error) {
      logError(error);
      setDisconnectError(getGoogleDisconnectErrorMessage(error));
    } finally {
      setDisconnectSubmitting(false);
    }
  };

  const connectButtonLabel = connectSubmitting
    ? "連携中..."
    : connectPhase === "authorizing"
      ? "認可待ち..."
      : statusRefreshing || googleStatus.state === "loading"
        ? "状態確認中..."
        : GOOGLE_CONNECT_START_BUTTON_LABEL;

  return (
    <>
      {showServiceSummary ? (
        <div className="mb-4 rounded-[1.4rem] border border-[#EADBC8] bg-[#FFF8F4] px-4 py-3 text-sm text-[#6A4F44]">
          対象サービス: <span className="font-black text-[#7C5A4E]">{scopeLabel}</span>
        </div>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        {showConnectButton && shouldShowConnectButton ? (
          <button
            type="button"
            onClick={() => void handleStartGoogleConnect(false)}
            disabled={interactionLocked || connectSubmitting || disconnectSubmitting}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#B07A55] px-5 py-3 text-sm font-black text-white transition hover:bg-[#986648] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {connectButtonLabel}
          </button>
        ) : null}

        {shouldShowDisconnectButton ? (
          <button
            type="button"
            onClick={handleOpenDisconnectConfirm}
            disabled={interactionLocked || connectSubmitting || disconnectSubmitting}
            className="inline-flex items-center justify-center rounded-full border border-[#E4D5C8] bg-white px-4 py-2 text-xs font-bold text-[#7C5A4E] transition hover:bg-[#FFF8F1] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {disconnectSubmitting ? "解除中..." : GOOGLE_DISCONNECT_BUTTON_LABEL}
          </button>
        ) : null}
      </div>

      {statusError ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {statusError}
        </div>
      ) : null}

      {disconnectSuccess ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#D8E7D3] bg-[#F7FBF3] px-4 py-3 text-sm text-[#58705A]">
          {disconnectSuccess}
        </div>
      ) : null}

      {disconnectError ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {disconnectError}
        </div>
      ) : null}

      {connectError ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {connectError}
        </div>
      ) : null}

      {connectPhase === "authorizing" ? (
        <div className="mt-4 rounded-[1.4rem] border border-[#EADBC8] bg-[#FFF8F4] px-4 py-3 text-sm text-[#7A655C]">
          Google の認可画面で連携または追加同意後、この画面に戻って状態が更新されます。
        </div>
      ) : null}

      <div className="mt-4 rounded-[1.4rem] border border-white/70 bg-white/80 p-4 text-sm leading-6 text-[#6A4F44]">
        {guideLines.map((line, index) => (
          <p key={`${scopeBundle}-${googleStatus.state}-${index}`}>{index + 1}. {line}</p>
        ))}
      </div>

      {disconnectConfirmOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center overflow-x-hidden bg-[#4A3E39]/35 px-4 sm:px-6">
          <div className="w-full max-w-sm rounded-[2rem] border border-[#E6DACD] bg-white p-6 shadow-[0_24px_60px_rgba(124,90,78,0.24)]">
            <p className="text-[12px] font-black uppercase tracking-[0.18em] text-[#B07A55]">Confirm</p>
            <h3 className="mt-2 text-xl font-black text-[#6A4F44]">Google連携を解除しますか？</h3>
            <p className="mt-3 text-sm leading-6 text-[#7A655C]">
              解除すると、この機能で必要なGoogle権限が使えなくなります。再度利用するには連携が必要です。
            </p>

            <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleCloseDisconnectConfirm}
                disabled={disconnectSubmitting}
                className="flex-1 rounded-full border border-[#E4D5C8] bg-white px-4 py-3 text-sm font-bold text-[#6A4F44] transition hover:bg-[#FFF8F1] disabled:cursor-not-allowed disabled:opacity-70"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleDisconnectGoogleIntegration}
                disabled={disconnectSubmitting}
                className="flex-1 rounded-full bg-[#A1493E] px-4 py-3 text-sm font-black text-white transition hover:bg-[#8E3E35] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {disconnectSubmitting ? "解除中..." : "解除する"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
